/** Monday 00:00:00.000 for the week containing `date` (UK week: Mon–Sun). */
export const getWeekStartMonday = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

/** True when `date` falls in the same calendar week (Mon–Sun) as `reference`. */
export const isSameCalendarWeek = (date, reference = new Date()) =>
  getWeekStartMonday(date).getTime() === getWeekStartMonday(reference).getTime();

const isChristmas2025 = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  return year === 2025 && ((month === 10 && day >= 29) || (month === 11 && day <= 30));
};

/** Deck the Halls / Christmas 2026: 14 Nov – 30 Dec. */
const isChristmas2026 = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  return year === 2026 && ((month === 10 && day >= 14) || (month === 11 && day <= 30));
};

/** Easter / spring half term 2026: castle open all week. */
const isSpringHalfTerm2026 = (date) =>
  date.getFullYear() === 2026 &&
  ((date.getMonth() === 2 && date.getDate() >= 30) ||
    (date.getMonth() === 3 && date.getDate() <= 10));

/** Animal Park daily opening 2026: 17 October – 1 November (castle stays Tue/Wed closed). */
export const ANIMAL_PARK_OCTOBER_HALF_TERM_2026 = {
  start: '2026-10-17',
  end: '2026-11-01',
  label: '17 October – 1 November 2026',
};

const toLocalYmd = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const isYmdInInclusiveRange = (ymd, start, end) => ymd >= start && ymd <= end;

/** Animal Park daily opening 2026: 17 October – 1 November. */
const isOctoberHalfTerm2026 = (date) =>
  isYmdInInclusiveRange(
    toLocalYmd(date),
    ANIMAL_PARK_OCTOBER_HALF_TERM_2026.start,
    ANIMAL_PARK_OCTOBER_HALF_TERM_2026.end
  );

/**
 * Castle closed until Christmas 2026 (re-opens with Deck the Halls on 14 November).
 * Gardens, Playground, Retail Village and Animal Park remain open.
 */
export const CASTLE_CLOSED_UNTIL_CHRISTMAS_2026 = {
  start: '2026-10-11',
  end: '2026-11-13',
  reopenLabel: 'Re-opens at Christmas',
  reopenDate: '14 November 2026',
};

/** True while the castle is closed ahead of its Christmas re-opening. */
export const isCastleClosedUntilChristmas = (date = new Date()) =>
  isYmdInInclusiveRange(
    toLocalYmd(date),
    CASTLE_CLOSED_UNTIL_CHRISTMAS_2026.start,
    CASTLE_CLOSED_UNTIL_CHRISTMAS_2026.end
  );

/** Dates when the castle is open seven days (overrides Tue/Wed closure). */
export const isCastleOpenSevenDaysOn = (date) =>
  isChristmas2025(date) ||
  isChristmas2026(date) ||
  isSpringHalfTerm2026(date);

/** True when Tue and Wed of this week are both in a seven-day-open period. */
export const isCastleOpenAllDaysThisWeek = (date = new Date()) => {
  const weekStart = getWeekStartMonday(date);
  const tuesday = new Date(weekStart);
  tuesday.setDate(weekStart.getDate() + 1);
  const wednesday = new Date(weekStart);
  wednesday.setDate(weekStart.getDate() + 2);
  return isCastleOpenSevenDaysOn(tuesday) && isCastleOpenSevenDaysOn(wednesday);
};

/** Castle display copy for hero / opening-times cards. */
export const getCastleOpeningDisplay = (cmsCastle = {}, referenceDate = new Date()) => {
  if (isCastleClosedUntilChristmas(referenceDate)) {
    return {
      days: 'Castle Closed',
      times: CASTLE_CLOSED_UNTIL_CHRISTMAS_2026.reopenLabel,
      note: `Castle re-opens ${CASTLE_CLOSED_UNTIL_CHRISTMAS_2026.reopenDate} · Gardens & Playground are still open`,
    };
  }

  if (isCastleOpenSevenDaysOn(referenceDate)) {
    return {
      days: 'Daily',
      times: cmsCastle.times || '10:00 - 17:00',
      note: 'Open daily · Last entry 16:00',
    };
  }

  return {
    days: 'Mon, Thu - Sun',
    times: cmsCastle.times || '10:00 - 17:00',
    note: 'Closed Tuesday and Wednesday · Last entry 16:00',
  };
};

/** Tour availability copy (tours depart from the castle). */
export const getCastleTourAvailability = (referenceDate = new Date()) =>
  isCastleClosedUntilChristmas(referenceDate)
    ? 'Castle closed – re-opens at Christmas'
    : 'Mon, Thu - Sun';

/** Whether Tue/Wed castle closure applies on this date. */
export const isCastleClosedTuesdayOrWednesday = (date) => {
  if (!date) return false;
  const day = date.getDay();
  if (day !== 2 && day !== 3) return false;
  return !isCastleOpenSevenDaysOn(date);
};

/** True when `date` falls in the Animal Park daily-opening period (17 Oct – 1 Nov 2026). */
export const isAnimalParkOctoberHalfTerm = (date = new Date()) =>
  isOctoberHalfTerm2026(date);

/** Open Saturdays and Sundays, plus every day 17 October – 1 November. */
export const isAnimalParkOpenOnDate = (date = new Date()) => {
  const day = date.getDay();
  return day === 0 || day === 6 || isAnimalParkOctoberHalfTerm(date);
};

/** True when Animal Park is in its daily-opening period (17 Oct – 1 Nov). */
export const isAnimalParkOpenAllDaysThisWeek = (date = new Date()) =>
  isAnimalParkOctoberHalfTerm(date);

/** Animal Park display copy for hero, info strips and page content. */
export const getAnimalParkOpeningDisplay = (referenceDate = new Date()) => {
  const times = '10:00 – 16:00';

  if (isAnimalParkOctoberHalfTerm(referenceDate)) {
    return {
      days: 'Daily',
      times,
      note: `Open every day ${ANIMAL_PARK_OCTOBER_HALF_TERM_2026.label} · Last entry 15:30`,
    };
  }

  const stillAnnounceHalfTerm =
    toLocalYmd(referenceDate) <= ANIMAL_PARK_OCTOBER_HALF_TERM_2026.end;

  return {
    days: 'Sat – Sun',
    times,
    note: stillAnnounceHalfTerm
      ? `Also open every day ${ANIMAL_PARK_OCTOBER_HALF_TERM_2026.label} · Last entry 15:30`
      : 'Last entry 15:30',
  };
};
